import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';


export interface UseFocusWithinPauseResult<TElement extends HTMLElement> {
  /** Debe asignarse al contenedor cuyo foco interno se quiere vigilar. */
  containerRef: RefObject<TElement | null>;
  /** `true` mientras el foco esté dentro del contenedor. */
  isInteracting: boolean;
}

// Indica si el foco está dentro del contenedor, por delegación de eventos.
export function useFocusWithinPause<
  TElement extends HTMLElement = HTMLDivElement,
>(): UseFocusWithinPauseResult<TElement> {
  const containerRef = useRef<TElement | null>(null);
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (): void => setIsInteracting(true);

    const handleFocusOut = (event: FocusEvent): void => {
      // Moverse entre elementos del mismo contenedor no termina la interacción.
      const nextFocused = event.relatedTarget as Node | null;
      if (nextFocused && container.contains(nextFocused)) return;
      setIsInteracting(false);
    };

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return { containerRef, isInteracting };
}

export default useFocusWithinPause;
